import { Link, ProgressBar, PropertyFilterProps, TableProps } from '@amzn/awsui-components-core';
import React from 'react';
import { ASR_DEFAULT_MESSAGE } from 'src/components/fintech-ops/risk-remediator/constants';
import { fetchOpenRisksForAsrApp } from 'src/components/fintech-ops/risk-remediator/utils';
import { convertUTCtoUserTimeZone } from 'src/utilities/DateTimeUtilities';

//_______________________________________________

export const RISK_REMEDIATOR_DETAILS_FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  {
    propertyLabel: 'Owner',
    key: 'owner_alias',
    groupValuesLabel: 'Owner Values',
    operators: ['=', '!=']
  },
  {
    propertyLabel: 'Resource name',
    key: 'resource_arn',
    groupValuesLabel: 'Resource Values',
    operators: ['=', '!=']
  },
  {
    propertyLabel: 'Manager',
    key: 'manager_alias',
    groupValuesLabel: 'Manager Values',
    operators: ['=', '!=']
  },
  {
    propertyLabel: 'Issue Details',
    key: 'issue_detected',
    groupValuesLabel: 'Issue details',
    operators: ['=', '!=']
  },
  {
    propertyLabel: 'Risk Classification',
    key: 'risk_classification',
    groupValuesLabel: 'Risk Classification Values',
    operators: ['=', '!=']
  }
];

export const RISK_DETAILS_VISIBLE_CONTENT = [
  'resource_arn',
  'resource_type',
  'issue_detected',
  'event_timestamp',
  'owner_alias',
  'manager_alias',
  'action_taken',
  'risk_classification'
];

export const getTableColDefs = (isOpenRisk: boolean): TableProps.ColumnDefinition<any>[] => {
  const decidingColumn: TableProps.ColumnDefinition<any> = isOpenRisk
    ? {
        id: 'pending_action',
        header: 'Remediation Details',
        cell: (item) => item.pending_action,
        minWidth: 250
      }
    : {
        id: 'action_taken',
        header: 'Remediation Details',
        cell: (item) => item.action_taken,
        minWidth: 250
      };

  return [...baseTableColDefsPrefix, decidingColumn, ...baseTableColDefsSuffix];
};

const baseTableColDefsPrefix: TableProps.ColumnDefinition<any>[] = [
  {
    id: 'issue_detected',
    header: 'Issue Details',
    cell: (item) => item.issue_detected,
    sortingField: 'issue_detected',
    minWidth: 400
  }
];

const baseTableColDefsSuffix: TableProps.ColumnDefinition<any>[] = [
  {
    id: 'owner_alias',
    header: 'Owner',
    cell: (item) => item.owner_alias,
    sortingField: 'owner_alias'
  },
  {
    id: 'manager_alias',
    header: 'Manager',
    cell: (item) => item.manager_alias,
    sortingField: 'manager_alias'
  },
  {
    id: 'resource_arn',
    header: 'Resource Name',
    cell: (item) => item.resource_arn,
    sortingField: 'resource_arn',
    isRowHeader: true,
    minWidth: 400
  },
  {
    id: 'resource_type',
    header: 'Resource Type',
    cell: (item) => item.resource_type,
    sortingField: 'resource_type'
  },
  {
    id: 'event_timestamp',
    header: 'Event timestamp',
    cell: (item) => convertUTCtoUserTimeZone(item.event_timestamp),
    sortingField: 'event_timestamp',
    minWidth: 200
  },
  {
    id: 'risk_classification',
    header: 'Risk Classification',
    cell: (item) => item.risk_classification,
    sortingField: 'risk_classification'
  }
];

export const summaryByReporteeColumnDef = [
  {
    id: 'login',
    header: 'Owner',
    cell: (item: any) => item.login,
    isRowHeader: true,
    minWidth: 250
  },
  {
    id: 'violationCount',
    header: 'Open Violation Count',
    cell: (item: any) => item.violationCount
  },
  {
    id: 'recommendationCount',
    header: 'Open Recommendations Count',
    cell: (item: any) => item.recommendationCount
  },
  {
    id: 'remediatedRiskCount',
    header: 'Auto Remediated Risk Count',
    cell: (item: any) => item.remediatedRiskCount
  },
  {
    id: 'devremediatedRiskCount',
    header: 'Developer Remediated Risk Count',
    cell: (item: any) => item.devRemediatedRiskCount
  },
  {
    id: 'asrAppCount',
    header: 'Total ASR app Count',
    cell: (item: any) => item.totalAppCount
  },
  {
    id: 'unCertifiedAsrAppCount',
    header: 'Uncertified App Count',
    cell: (item: any) => item.unCertifiedAsrAppCount
  }
];

export const asrColumnDef = [
  {
    id: 'applicationName',
    header: 'Application Name',
    cell: (item: any) => (
      <Link external href={`https://asr.security.amazon.dev/applications/${item.application_id}/details`}>
        {' '}
        {item.asr_application_name}{' '}
      </Link>
    ),
    isRowHeader: true,
    minWidth: 350,
    sortingField: 'asr_application_name'
  },
  {
    id: 'reviewOwner',
    header: 'Review Owner',
    cell: (item: any) => item.asr_review_owner,
    sortingField: 'asr_review_owner',
    minWidth: 200
  },
  {
    id: 'appOwner',
    header: 'ASR Point Of Contact',
    cell: (item: any) => item.asr_app_owner,
    sortingField: 'asr_app_owner',
    minWidth: 200
  },
  {
    id: 'manager',
    header: 'Application Manager',
    cell: (item: any) => item.application_owner,
    sortingField: 'application_manager',
    minWidth: 200
  },
  {
    id: 'certificationStatus',
    header: 'Certification Status',
    cell: (item: any) => item.application_certification_status.replaceAll('_', ' '),
    sortingField: 'application_certification_status',
    minWidth: 300
  },
  {
    id: 'applicationCreationDate',
    header: 'ASR Creation Date',
    cell: (item: any) => item.application_creation_date,
    sortingField: 'application_creation_date',
    minWidth: 200
  },
  {
    id: 'releaseDate',
    header: 'Release Date',
    cell: (item: any) => item.application_release_date || ASR_DEFAULT_MESSAGE.notApplicable,
    sortingField: 'application_release_date',
    minWidth: 200
  },
  {
    id: 'dataClassification',
    header: 'Data Classification',
    cell: (item: any) => item.data_classification?.replaceAll('_', ' ') || ASR_DEFAULT_MESSAGE.dataNotClassified,
    sortingField: 'data_classification',
    minWidth: 300
  }
];

export const RISK_REMEDIATOR_ASR_FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  {
    propertyLabel: 'Owner',
    key: 'owner_alias',
    groupValuesLabel: 'Owner Values',
    operators: ['=', '!=']
  },
  {
    propertyLabel: 'Application name',
    key: 'asr_application_name',
    groupValuesLabel: 'Application Values',
    operators: ['=', '!=']
  },
  {
    propertyLabel: 'Manager',
    key: 'manager_alias',
    groupValuesLabel: 'Manager Values',
    operators: ['=', '!=']
  },
  {
    propertyLabel: 'Certification Status',
    key: 'application_certification_status',
    groupValuesLabel: 'Application Certification Values',
    operators: ['=', '!=']
  },
  {
    propertyLabel: 'Certification Status',
    key: 'application_certification_status',
    groupValuesLabel: 'Application Certification Values',
    operators: ['=', '!=']
  },
  {
    propertyLabel: 'Certification Status',
    key: 'application_certification_status',
    groupValuesLabel: 'Application Certification Values',
    operators: ['=', '!=']
  },
  {
    propertyLabel: 'Review Progress Status',
    key: 'review_progress_percentage',
    groupValuesLabel: 'Review Progress Status Values',
    operators: ['=', '!=']
  },
  {
    propertyLabel: 'Review Classification',
    key: 'review_classification',
    groupValuesLabel: 'Review Classification Values',
    operators: ['=', '!=']
  }
];

export const vpcColumnDef = [
  {
    id: 'vpc_id',
    header: 'VPC ID',
    cell: (item: any) => item.vpc_id,
    isRowHeader: true,
    minWidth: 350,
    sortingField: 'vpc_id'
  },
  {
    id: 'account_detail',
    header: 'AWS Account Detail',
    cell: (item: any) => item.account_detail,
    sortingField: 'account_detail',
    minWidth: 200
  },
  {
    id: 'cidr_ip_block',
    header: 'CIDR',
    cell: (item: any) => item.cidr_ip_block,
    sortingField: 'cidr_ip_block',
    minWidth: 200
  },
  {
    id: 'used_ip_count',
    header: 'In Use IP count',
    cell: (item: any) => item.used_ip_count,
    sortingField: 'used_ip_count',
    minWidth: 200
  },
  {
    id: 'unused_ip_count',
    header: 'Unused IP Count',
    cell: (item: any) => item.unused_ip_count,
    sortingField: 'unused_ip_count',
    minWidth: 200
  },
  {
    id: 'ip_usage_percentage',
    header: 'IP Utilization Percentage',
    cell: (item: any) => item.ip_usage_percentage,
    sortingField: 'ip_usage_percentage',
    minWidth: 300
  },
  {
    id: 'team_poc',
    header: 'Owner',
    cell: (item: any) => item.team_poc,
    sortingField: 'team_poc',
    minWidth: 300
  }
];

export const RISK_REMEDIATOR_VPC_FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  {
    propertyLabel: 'Owner',
    key: 'team_poc',
    groupValuesLabel: 'Owner Values',
    operators: ['=', '!=']
  },
  {
    propertyLabel: 'Account Details',
    key: 'account_detail',
    groupValuesLabel: 'AWS Account Detail Values',
    operators: ['=', '!=']
  },
  {
    propertyLabel: 'VPC ID',
    key: 'vpc_id',
    groupValuesLabel: 'Vpc Id Values',
    operators: ['=', '!=']
  },
  {
    propertyLabel: 'CIDR IP',
    key: 'cidr_ip_block',
    groupValuesLabel: 'CIDR IP Values',
    operators: ['=', '!=']
  }
];
export const topRiskCategoriesColumnDef = [
  {
    id: 'risk_category',
    header: 'Frequently Identified Risks (Top 5)',
    cell: (item: any) => item.title,
    isRowHeader: true,
    width: '70%'
  },
  {
    id: 'risk_count',
    header: 'Risk Count',
    cell: (item: any) => item.value,
    isRowHeader: true
  }
];
